import React from "react";
import deburr from "lodash/deburr";
import Autosuggest from "react-autosuggest";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import TextField from "@material-ui/core/TextField";
import Paper from "@material-ui/core/Paper";
import MenuItem from "@material-ui/core/MenuItem";
import { makeStyles, createTheme } from "@material-ui/core/styles";
import { ThemeProvider } from "@material-ui/styles";
import { membershipAPI } from "../../../../../API/API-membership";

let suggestions = [];
membershipAPI.GET.getAllMemberships()
  .then((success) => {
    suggestions = success.data;
  })
  .catch((error) => {
    console.log(error);
  });

function getMatchingFamilyMembers(member, inputValue) {
  const familyMembers = member.familyMembersInformation || [];
  if (!inputValue) return familyMembers[0] ? [familyMembers[0]] : [];
  return familyMembers.filter((item) => {
    const name = deburr(`${item.firstName} ${item.lastName}`).toLowerCase();
    return name.includes(inputValue);
  });
}

// Membership IDs and cards do not contain whitespace, so a multi-word
// query is always a name search and must not match by ID/card —
// otherwise unrelated memberships whose IDs happen to contain a name
// substring would pollute the dropdown with their primary contact.
function looksLikeIdOrCard(inputValue) {
  return inputValue.length > 0 && !/\s/.test(inputValue);
}

function renderInputComponent(inputProps) {
  const { classes, inputRef = () => {}, ref, ...other } = inputProps;

  return (
    <TextField
      fullWidth
      InputProps={{
        inputRef: (node) => {
          ref(node);
          inputRef(node);
        },
        classes: {
          input: classes.input,
        },
      }}
      {...other}
    />
  );
}

function renderSuggestion(suggestion, { query, isHighlighted }) {
  // Each suggestion is built per matching family member by getSuggestions,
  // so matchedFamilyMember should always be set. Fall back to a live
  // query-driven lookup before the primary so a dropped property never
  // collapses the row back to the primary contact.
  const inputValue = deburr(query.trim()).toLowerCase();
  const matched = suggestion.matchedFamilyMember
    || (inputValue ? getMatchingFamilyMembers(suggestion, inputValue)[0] : null)
    || suggestion.familyMembersInformation[0];
  const displayName = `${matched.firstName} ${matched.lastName}`;
  const matches = match(displayName, query);
  const parts = parse(`${displayName} `, matches);

  return (
    <MenuItem selected={isHighlighted} component="div">
      <div>
        {parts.map((part) => (
          <span
            key={part.text}
            style={{ fontWeight: part.highlight ? 500 : 400 }}
          >
            {part.text}
          </span>
        ))}
      </div>
    </MenuItem>
  );
}

const searchByMembershipCard = (member, inputValue) => {
  if (!member.familyMembersInformation[0]) return false;
  if (!member.familyMembersInformation[0].membershipCard) return false;
  if (member.familyMembersInformation[0].membershipCard.includes(inputValue))
    return true;
  return false;
};

function getSuggestions(value) {
  const inputValue = deburr(value.trim()).toLowerCase();
  const inputLength = inputValue.length;
  if (inputLength === 0) return [];

  const results = [];
  let count = 0;

  // search by first, lastName, membershipID and membershipCard
  // produce one entry per matching family member so the dropdown
  // displays the searched person, not just the primary
  suggestions.forEach((member) => {
    if (count >= 100) return;
    if (member.status === "inactive" || member.status === "expired") return;
    if (!member.familyMembersInformation || !member.familyMembersInformation[0]) return;

    const matchingFamilyMembers = getMatchingFamilyMembers(member, inputValue);
    if (matchingFamilyMembers.length > 0) {
      matchingFamilyMembers.forEach((familyMember) => {
        if (count >= 100) return;
        results.push({ ...member, matchedFamilyMember: familyMember });
        count += 1;
      });
      return;
    }

    if (!looksLikeIdOrCard(inputValue)) return;
    const matchedByCardOrId = searchByMembershipCard(member, inputValue)
      || member.familyMembersInformation[0].membershipID
        .toLowerCase()
        .includes(inputValue);
    if (matchedByCardOrId) {
      results.push({ ...member, matchedFamilyMember: member.familyMembersInformation[0] });
      count += 1;
    }
  });

  return results;
}

// send the instructor found to the parent component
let memberData = "";
function getSuggestionValue(suggestion) {
  const matched = suggestion.matchedFamilyMember || suggestion.familyMembersInformation[0];
  const { matchedFamilyMember, ...membership } = suggestion;
  memberData = { ...membership, selectedFamilyMember: matched };
  return `${matched.firstName} ${matched.lastName} `;
}
const theme = createTheme({
  palette: {
    primary: {
      main: "#279d8f",
    },
  },
});

const useStyles = makeStyles((theme) => ({
  root: {
    height: 50,
    flexGrow: 1,
    zIndex: 100,
    position: "relative",
  },
  container: {
    position: "relative",
  },
  suggestionsContainerOpen: {
    position: "absolute",
    zIndex: 100,
    marginTop: theme.spacing(1),
    left: 0,
    right: 0,
  },
  suggestion: {
    display: "block",
  },
  suggestionsList: {
    margin: 0,
    padding: 0,
    listStyleType: "none",
  },
  divider: {
    height: theme.spacing(2),
  },
  paper: {
    zIndex: 100,
  },
}));

export default function IntegrationAutosuggest(props) {
  const classes = useStyles();
  const [state, setState] = React.useState({
    single: "",
    popper: "",
  });

  const [stateSuggestions, setSuggestions] = React.useState([]);

  const handleSuggestionsFetchRequested = ({ value }) => {
    setSuggestions(getSuggestions(value));
  };

  const handleSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const handleChange = (name) => (event, { newValue }) => {
    setState({
      ...state,
      [name]: newValue,
    });
 
    if (props.typeOfSearch === "member") {
      props.handleMemberFound(memberData);
    } else {
      props.handleFamilyMemberFound(memberData);
    }

    memberData = "";
  };

  const autosuggestProps = {
    renderInputComponent,
    suggestions: stateSuggestions,
    onSuggestionsFetchRequested: handleSuggestionsFetchRequested,
    onSuggestionsClearRequested: handleSuggestionsClearRequested,
    getSuggestionValue,
    renderSuggestion,
  };

  return (
    <div className={classes.root}>
      <ThemeProvider theme={theme}>
        <Autosuggest
          {...autosuggestProps}
          inputProps={{
            classes,
            id: memberData.firstName,
            label: props.label,
            placeholder: props.label,
            value: state.single,
            name: props.label,
            onChange: handleChange("single"),
            variant: "filled",
            helperText: props.helperText,
            error: props.error === "" ? false : props.error,
          }}
          theme={{
            container: classes.container,
            suggestionsContainerOpen: classes.suggestionsContainerOpen,
            suggestionsList: classes.suggestionsList,
            suggestion: classes.suggestion,
          }}
          renderSuggestionsContainer={(options) => (
            <Paper
              {...options.containerProps}
              square
              className={classes.paper}
              style={{ overflow: "scroll", maxHeight: "200px" }}
            >
              {options.children}
            </Paper>
          )}
        />
      </ThemeProvider>
    </div>
  );
}
